import { Sparkles, TrendingUp, Zap } from 'lucide-react';

interface JsonToonCardProps {
  userName: string;
  userEmail: string;
  monthlyRequests: number;
  lifetimeRequests: number;
  monthlyTokensSaved: number;
  lifetimeTokensSaved: number;
  memberSince: string;
  tier: 'FREE' | 'PRO';
}

export default function JsonToonCard({
  userName,
  userEmail,
  monthlyRequests,
  lifetimeRequests,
  monthlyTokensSaved,
  lifetimeTokensSaved,
  memberSince,
  tier,
}: JsonToonCardProps) {
  // Get user initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format large numbers
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Format member since date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="relative">
      {/* Main card */}
      <div className="bg-gradient-to-br from-coral-500 via-pink-500 to-teal-500 rounded-3xl p-8 shadow-xl">
        {/* Content */}
        <div>
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-4">
              {/* User avatar */}
              <div className="w-16 h-16 bg-white/30 rounded-2xl flex items-center justify-center shadow-md">
                <span className="text-2xl font-bold text-white">
                  {getInitials(userName)}
                </span>
              </div>

              {/* User info */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">{userName}</h3>
                <p className="text-sm text-white/90">{userEmail}</p>
              </div>
            </div>

            {/* Tier badge */}
            <div className={`px-4 py-2 rounded-full shadow-md ${
              tier === 'PRO'
                ? 'bg-amber-400/40 border-2 border-white/40'
                : 'bg-white/30 border-2 border-white/40'
            }`}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-sm font-bold text-white">{tier}</span>
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Monthly usage */}
            <div className="bg-white/20 rounded-2xl p-4 border-2 border-white/30">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-white" />
                <p className="text-xs text-white/90 font-medium uppercase tracking-wide">This Month</p>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{formatNumber(monthlyRequests)}</p>
              <p className="text-xs text-white/80">requests</p>
            </div>

            {/* Lifetime usage */}
            <div className="bg-white/20 rounded-2xl p-4 border-2 border-white/30">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-white" />
                <p className="text-xs text-white/90 font-medium uppercase tracking-wide">Lifetime</p>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{formatNumber(lifetimeRequests)}</p>
              <p className="text-xs text-white/80">requests</p>
            </div>
          </div>

          {/* Token savings */}
          <div className="bg-white/20 rounded-2xl p-4 border-2 border-white/30 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-amber-300" />
              <p className="text-sm text-white font-semibold">Token Savings</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-white/80 mb-1">This Month</p>
                <p className="text-2xl font-bold text-white">{formatNumber(monthlyTokensSaved)}</p>
              </div>
              <div>
                <p className="text-xs text-white/80 mb-1">Lifetime</p>
                <p className="text-2xl font-bold text-white">{formatNumber(lifetimeTokensSaved)}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <div>
                <p className="text-xs text-white/70">JsonToonCraft</p>
                <p className="text-sm text-white font-semibold">Member since {formatDate(memberSince)}</p>
              </div>
            </div>

            {/* Chip design */}
            <div className="w-12 h-10 bg-amber-400/30 rounded-lg border-2 border-white/40 shadow-md"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
